import { useState } from 'react';
import { Download } from 'lucide-react';
import { exportXlsx, type SheetSpec } from '../lib/xlsx-export';
import { useToast } from '../contexts/ToastContext';

/**
 * Page-level "Export Excel" button. Pages provide a `sheets` builder
 * that returns one or more worksheets — multi-tab pages should produce
 * one sheet per tab. The button handles busy state and toast errors,
 * and the underlying xlsx library is loaded only when first clicked.
 */
export default function ExcelExportButton({
  filename,
  sheets,
  disabled,
  label,
  small,
}: {
  filename: string;
  sheets: () => SheetSpec[] | Promise<SheetSpec[]>;
  disabled?: boolean;
  label?: string;
  small?: boolean;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const s = await sheets();
      if (!s.length || s.every((sh) => sh.rows.length <= 1)) {
        toast.info('Nothing to export — no rows on the current view');
        return;
      }
      await exportXlsx(filename, s);
    } catch (err) {
      toast.error(`Export failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={onClick}
      disabled={busy || disabled}
      className={`btn btn-secondary flex items-center gap-2 disabled:opacity-40 ${
        small ? 'py-1 px-2 text-xs' : 'text-sm'
      }`}
      title="Download as Excel — one tab per worksheet"
    >
      <Download size={small ? 12 : 14} />
      {busy ? 'Exporting…' : label ?? 'Export Excel'}
    </button>
  );
}
